/**
 * StrikeZoneHeatmap - Strike zone visualization with heat map overlay
 *
 * Features:
 * - 3x3 or 5x5 zone grid
 * - Color intensity based on frequency/tipping rate
 * - Interactive zone selection
 * - Pitch location scatter plot overlay
 * - Responsive sizing
 *
 * Based on professional baseball analytics visualizations
 */

import React, { useState } from 'react';

// Color scales for different metrics
const HEAT_COLORS = {
  frequency: [
    { threshold: 0, color: 'rgba(59, 130, 246, 0.1)' },   // Blue - cold
    { threshold: 5, color: 'rgba(59, 130, 246, 0.3)' },
    { threshold: 10, color: 'rgba(34, 197, 94, 0.4)' },   // Green
    { threshold: 20, color: 'rgba(234, 179, 8, 0.5)' },   // Yellow
    { threshold: 30, color: 'rgba(249, 115, 22, 0.6)' },  // Orange
    { threshold: 40, color: 'rgba(239, 68, 68, 0.7)' },   // Red - hot
    { threshold: 50, color: 'rgba(239, 68, 68, 0.85)' },
  ],
  tipping: [
    { threshold: 0, color: 'rgba(34, 197, 94, 0.3)' },    // Green - clean
    { threshold: 15, color: 'rgba(234, 179, 8, 0.4)' },   // Yellow
    { threshold: 25, color: 'rgba(249, 115, 22, 0.5)' },  // Orange
    { threshold: 35, color: 'rgba(239, 68, 68, 0.6)' },   // Red
    { threshold: 50, color: 'rgba(239, 68, 68, 0.8)' },   // Hot red
  ],
};

function getHeatColor(value, colorScale = 'frequency') {
  const scale = HEAT_COLORS[colorScale] || HEAT_COLORS.frequency;
  for (let i = scale.length - 1; i >= 0; i--) {
    if (value >= scale[i].threshold) {
      return scale[i].color;
    }
  }
  return scale[0].color;
}

/**
 * StrikeZoneHeatmap - Main heatmap component
 */
export default function StrikeZoneHeatmap({
  data = {},
  pitchLocations = [],
  size = 200,
  gridSize = 3,
  metric = 'frequency',
  showValues = true,
  showBorder = true,
  title = 'Location',
  subtitle = '',
  onZoneClick,
  className = '',
}) {
  const [hoveredZone, setHoveredZone] = useState(null);

  const cellSize = size / gridSize;
  const borderWidth = 2;

  // Generate zone data from pitch locations if not provided
  const zoneData = data.zones || generateZoneData(pitchLocations, gridSize, metric);

  // Zone labels (1-9 for 3x3 grid)
  const getZoneLabel = (row, col) => {
    if (gridSize === 3) {
      return (row * 3 + col + 1).toString();
    }
    return `${row + 1}-${col + 1}`;
  };

  return (
    <div className={`inline-flex flex-col items-center ${className}`}>
      {/* Title */}
      {title && (
        <div className="mb-2 text-center">
          <h4 className="text-sm font-semibold text-white uppercase tracking-wider">{title}</h4>
          {subtitle && <p className="text-xs text-zinc-500">{subtitle}</p>}
        </div>
      )}

      {/* Strike Zone Grid */}
      <div
        className="relative bg-zinc-900 rounded-lg overflow-hidden"
        style={{ width: size, height: size }}
      >
        {/* Strike zone border */}
        {showBorder && (
          <div
            className="absolute inset-0 border-2 border-zinc-600 rounded-lg pointer-events-none z-10"
            style={{
              boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.1)',
            }}
          />
        )}

        {/* Grid cells */}
        <div
          className="grid w-full h-full"
          style={{
            gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
            gridTemplateRows: `repeat(${gridSize}, 1fr)`,
            gap: '1px',
          }}
        >
          {Array.from({ length: gridSize * gridSize }).map((_, idx) => {
            const row = Math.floor(idx / gridSize);
            const col = idx % gridSize;
            const zoneKey = `${row}-${col}`;
            const zoneValue = zoneData[zoneKey] || 0;
            const heatColor = getHeatColor(zoneValue, metric);
            const isHovered = hoveredZone === zoneKey;

            return (
              <button
                key={zoneKey}
                className={`relative flex items-center justify-center transition-all duration-200 ${
                  onZoneClick ? 'cursor-pointer hover:brightness-125' : ''
                } ${isHovered ? 'ring-2 ring-white ring-opacity-50 z-10' : ''}`}
                style={{
                  backgroundColor: heatColor,
                }}
                onClick={() => onZoneClick?.(zoneKey, zoneValue)}
                onMouseEnter={() => setHoveredZone(zoneKey)}
                onMouseLeave={() => setHoveredZone(null)}
              >
                {showValues && zoneValue > 0 && (
                  <span className="text-white font-mono font-bold text-sm drop-shadow-lg">
                    {Math.round(zoneValue)}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Pitch location dots (scatter plot overlay) */}
        {pitchLocations.length > 0 && (
          <div className="absolute inset-0 pointer-events-none">
            {pitchLocations.slice(0, 50).map((pitch, idx) => {
              // Normalize coordinates to 0-1 range
              const x = (pitch.x ?? 0.5) * size;
              const y = (pitch.y ?? 0.5) * size;
              const color = pitch.tipping
                ? 'rgba(239, 68, 68, 0.9)'
                : 'rgba(34, 197, 94, 0.9)';

              return (
                <div
                  key={idx}
                  className="absolute w-2 h-2 rounded-full transform -translate-x-1/2 -translate-y-1/2"
                  style={{
                    left: x,
                    top: y,
                    backgroundColor: color,
                    border: '1px solid rgba(255,255,255,0.5)',
                  }}
                  title={pitch.pitchType || 'Pitch'}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Zone breakdown (optional) */}
      {hoveredZone && zoneData[hoveredZone] !== undefined && (
        <div className="mt-2 px-3 py-1.5 bg-zinc-800 rounded text-xs">
          <span className="text-zinc-400">Zone {hoveredZone}: </span>
          <span className="text-white font-mono font-bold">
            {Math.round(zoneData[hoveredZone])}
            {metric === 'tipping' ? '%' : ''}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * Generate zone data from pitch locations
 */
function generateZoneData(pitchLocations, gridSize, metric) {
  const zones = {};

  // Initialize zones
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      zones[`${row}-${col}`] = { count: 0, tipping: 0 };
    }
  }

  // Count pitches per zone
  pitchLocations.forEach((pitch) => {
    const col = Math.min(gridSize - 1, Math.floor((pitch.x ?? 0.5) * gridSize));
    const row = Math.min(gridSize - 1, Math.floor((pitch.y ?? 0.5) * gridSize));
    const zoneKey = `${row}-${col}`;

    if (zones[zoneKey]) {
      zones[zoneKey].count++;
      if (pitch.tipping) zones[zoneKey].tipping++;
    }
  });

  // Calculate metric values
  const total = pitchLocations.length || 1;
  const result = {};

  Object.entries(zones).forEach(([key, zone]) => {
    if (metric === 'frequency') {
      result[key] = (zone.count / total) * 100;
    } else if (metric === 'tipping') {
      result[key] = zone.count > 0 ? (zone.tipping / zone.count) * 100 : 0;
    } else {
      result[key] = zone.count;
    }
  });

  return result;
}

/**
 * DualZoneDisplay - Side by side strike zones for comparison
 */
export function DualZoneDisplay({
  leftData,
  rightData,
  leftTitle = 'Fastball',
  rightTitle = 'Off-speed',
  size = 150,
  metric = 'tipping',
}) {
  return (
    <div className="flex items-start gap-6">
      <StrikeZoneHeatmap
        data={{ zones: leftData }}
        size={size}
        metric={metric}
        title={leftTitle}
        showValues={true}
      />
      <StrikeZoneHeatmap
        data={{ zones: rightData }}
        size={size}
        metric={metric}
        title={rightTitle}
        showValues={true}
      />
    </div>
  );
}

/**
 * LocationSummaryCard - Card with heatmap and summary stats
 */
export function LocationSummaryCard({
  zoneData,
  pitchType = 'All',
  totalPitches = 0,
  tippingRate = 0,
  className = '',
}) {
  return (
    <div className={`bg-zinc-800/50 border border-zinc-700 rounded-xl p-4 ${className}`}>
      <div className="flex items-start gap-4">
        <StrikeZoneHeatmap
          data={{ zones: zoneData }}
          size={120}
          metric="tipping"
          title=""
          showValues={false}
        />

        <div className="flex-1">
          <h4 className="text-lg font-bold text-white mb-2">{pitchType}</h4>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-400">Pitches</span>
              <span className="text-white font-mono">{totalPitches}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-400">Tipping Rate</span>
              <span
                className="font-mono font-bold"
                style={{
                  color: tippingRate > 30 ? '#ef4444' : tippingRate > 15 ? '#f97316' : '#22c55e',
                }}
              >
                {(tippingRate * 100).toFixed(0)}%
              </span>
            </div>
          </div>

          {/* Hot zones indicator */}
          <div className="mt-3 pt-3 border-t border-zinc-700">
            <p className="text-xs text-zinc-500 mb-1">Highest Tipping Zones:</p>
            <div className="flex gap-1">
              {Object.entries(zoneData)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 3)
                .map(([zone, value]) => (
                  <span
                    key={zone}
                    className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded font-mono"
                  >
                    {zone}: {Math.round(value)}%
                  </span>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
